/**
 * A TÍPUSJAVÍTÁS MAGJA (#92) — tiszta függvények, React nélkül.
 *
 * ═══ GOMBSOR VAGY LENYÍLÓ — A DARABSZÁM DÖNT ═══
 *
 * Viktor döntése (2026-11-02, `docs/tervezes/design/tipusjavitas/`): a típus
 * a „Megváltoztatom" űrlap első sora. Hat típusig GOMBSOR — mind látszik, és
 * egy számbillentyű választ. Hat fölött LENYÍLÓ, gépeléssel szűrve: a gombsor
 * ott már nem fér ki, és a számbillentyűk is elfogynának. A billentyűk
 * mindkettőben ugyanazt jelentik (nyíl választ, `Enter` ment, `Esc` elvet),
 * tehát a váltás nem kíván újratanulást.
 *
 * ⚠️ NEM A BÖNGÉSZŐ `<select>`-JE. A D9 tiltja: billentyűvel csapda,
 * megnyílik és elnyeli a leütéseket.
 */

import type { DocType } from '@/i18n/generated';

/**
 * AMIT A KÖNYVELŐ VÁLASZTHAT — a backend `CLIENT_DECLARABLE_DOC_TYPES`-ának
 * tükre (`document.controller.ts`). A szerver ugyanezt a készletet fogadja el
 * a `PATCH /documents/:id/type-correction`-ön; ami itt több, az `400`-at kap.
 *
 * A `BANK_STATEMENT` SZÁNDÉKOSAN NINCS BENNE: a kivonat más úton, más
 * formában érkezik — egy átminősített számla attól nem lesz kivonat.
 *
 * A SORREND A FELÜLETÉ: a számbillentyű a pozíciót választja.
 */
export const CORRECTABLE_DOC_TYPES: readonly DocType[] = [
  'INBOUND_INVOICE',
  'OUTBOUND_INVOICE',
  'RECEIPT',
  'OTHER',
];

/**
 * A VÁLASZTÓ KÉSZLETE EGY BIZONYLATRA — a cég engedélye szerint (Viktor,
 * 2026-11-03), a felület sorrendjében.
 *
 * ⚠️ A JELENLEGI TÍPUS AKKOR IS BENNE VAN, ha a cégnél már nincs engedélyezve
 * (az engedély az ÚJ besorolást szűkíti). Nélküle a gombsorban egyik gomb sem
 * állna kijelölve, és a könyvelő nem látná, honnan javít. Kiválasztva sem
 * baj: az azonos típus nem javítás.
 */
export function typeOptionsFor(allowed: readonly DocType[], current: DocType): DocType[] {
  return CORRECTABLE_DOC_TYPES.filter(
    (docType) => allowed.includes(docType) || docType === current
  );
}

/** Eddig a darabszámig gombsor; fölötte lenyíló. */
export const SEGMENTED_MAX = 6;

export type TypeControl = 'segmented' | 'combobox';

export function typeControlFor(count: number): TypeControl {
  return count <= SEGMENTED_MAX ? 'segmented' : 'combobox';
}

/**
 * A SZÁMBILLENTYŰ → TÍPUS. `null`, ha a leütés nem választás.
 *
 * Csak a puszta számjegy: a `Ctrl+1` a felületváltás (D9), azt nem vehetjük
 * el. És csak annyi, ahány típus van — a `7` hat típusnál semmit nem jelent.
 */
export function typeForDigit(
  key: string,
  modifiers: { ctrlKey: boolean; altKey: boolean; metaKey: boolean },
  options: readonly DocType[]
): DocType | null {
  if (modifiers.ctrlKey || modifiers.altKey || modifiers.metaKey) {
    return null;
  }
  if (!/^[1-9]$/.test(key)) {
    return null;
  }
  return options[Number(key) - 1] ?? null;
}

/**
 * A LENYÍLÓ SZŰRŐJE: a felirat bárhol tartalmazza a begépeltet, kis- és
 * nagybetű, ékezet nélkül is („kimeno" → Kimenő számla). Az üres szűrő
 * mindent mutat.
 */
export function filterTypes(
  query: string,
  options: readonly DocType[],
  labelOf: (docType: DocType) => string
): DocType[] {
  const needle = fold(query.trim());
  if (needle === '') {
    return [...options];
  }
  return options.filter((docType) => fold(labelOf(docType)).includes(needle));
}

function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/** A kimenő számlán a partner a VEVŐ, és az adószáma nem minimum (#51). */
export function isOutbound(docType: DocType): boolean {
  return docType === 'OUTBOUND_INVOICE';
}
